import { FormEvent, useState } from 'react';
import useTableOrder from 'hooks/tableOrder';
import { Doctor } from 'types/doctor';
import { api } from 'services/api';
import { Paginated } from 'types/paginated';

export type DoctorQueryParams = {
  active: boolean;
  name: string;
  document_number: string;
  crm: string;
};

export function useFetchDoctors() {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState<number>(96);
  const [total, setTotal] = useState<number>(0);
  const [orderedIndex, sort] = useTableOrder();
  const [filter, setFilter] = useState<DoctorQueryParams>({
    document_number: '',
    name: '',
    crm: '',
    active: true,
  });

  function fetchData(nextPage = page, nextRows = rows, nextFilter = filter) {
    setLoading(true);

    api
      .get<Paginated<Doctor[]>>('/doctors', {
        params: {
          ...nextFilter,
          active: nextFilter.active ? 1 : 0,
          page: nextPage + 1,
          rows: nextRows,
        },
      })
      .then(response => {
        setTotal(response.data.total);
        const _doctors = response.data.items.map(doctor => ({
          ...doctor,
          formattedActive: doctor.active ? 'Sim' : 'Não',
          formattedCity: doctor.address?.city || 'Não informado',
          formattedState: doctor.address?.state || 'Não informado',
        }));
        setDoctors(_doctors);
      })
      .finally(() => setLoading(false));
  }

  function handleSearch(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    setPage(0);
    fetchData(0, rows, filter);
  }

  function handleChangePage(nextPage: number) {
    setPage(nextPage);
    fetchData(nextPage, rows, filter);
  }

  function handleChangeRows(nextRows: number) {
    setRows(nextRows);
    setPage(0);
    fetchData(0, nextRows, filter);
  }

  function handleSort(index: string) {
    const p = sort(index, doctors);
    setDoctors(p);
  }

  function handleChange(index: keyof DoctorQueryParams, value: string) {
    setFilter(state => ({
      ...state,
      [index]: value,
    }));
  }

  return {
    doctors,
    loading,
    orderedIndex,
    handleSort,
    filter,
    handleChange,
    setPage: handleChangePage,
    setRows: handleChangeRows,
    handleSearch,
    page,
    rows,
    total,
  };
}
